import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { UPLOAD_MEDIA } from "../reducers/authReducer";
import { pushNotification } from "utils/notifications";

/**
 * The media gallery's own upload path, lifted out of MessagePanel so the
 * gallery keeps working now that it renders in the map column instead.
 *
 * The browser's geolocation API hands back `{latitude, longitude}`, but every
 * consumer in this app — mapSagas included — reads `{lat, long}`. Uploads used
 * to store the raw shape, so media records carried coordinates nothing could
 * read and never surfaced on the global map. That conversion has to travel with
 * the upload, which is the other reason this is a hook and not a copy.
 */
const toBeamCoordinates = (location?: CurrentLocation) => ({
  lat: location?.latitude || 0,
  long: location?.longitude || 0,
});

export const useRawMediaUpload = (selectedItem: any) => {
  const dispatch = useDispatch();
  const [currentLocation, setCurrentLocation] = useState<CurrentLocation>();

  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition((position) => {
      setCurrentLocation({
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
      });
    });
  }, []);

  // Progress is tracked but not surfaced here: the gallery shows tiles, not the
  // chat footer's per-file bar. Kept so a caller that wants it has it.
  const [uploadProgress, setUploadProgress] = useState<any>({});

  const uploadRawFiles = (rawFiles: File[]) => {
    rawFiles.forEach((file) => {
      let form = new FormData();
      form.append("file", file);
      form.append(
        "info",
        JSON.stringify({
          pageId: selectedItem?.pageId,
          userId: localStorage.getItem("RID"),
          location: currentLocation
            ? {
                coordinates: toBeamCoordinates(currentLocation),
                clbs: {
                  tags: [
                    "type:media",
                    `org:${selectedItem?.persistent?.static?.iotAsset?.config?.configUserCudbServiceId}`,
                    `name:${file.name}`,
                  ],
                },
              }
            : undefined,
        })
      );

      dispatch(
        UPLOAD_MEDIA({
          form,
          isBio: undefined,
          onProgress: (progress) => {
            setUploadProgress((prevProgress) => ({
              ...prevProgress,
              [file.name]: progress,
            }));
          },
          onError: () => {
            pushNotification(
              `An error occurred while uploading file ${file.name}`,
              "error"
            );
          },
        })
      );
    });
  };

  return { uploadRawFiles, uploadProgress };
};

export default useRawMediaUpload;
